import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";

import { Button } from "@/core/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/core/components/ui/card";
import { NoTranslate } from "@/core/components/ui/no-translate";
import useTypedTranslation, {
  type TFunctionType,
} from "@/core/hooks/use-typed-translation";
import { DEFAULT_CURRENCY } from "@/core/utils/func/money-minor";
import { AmountMinor } from "@/features/shared";
import { useWallets } from "@/features/wallet/api/use-wallets";
import { useWalletBalance } from "@/features/wallet/api/use-wallet-balance";
import {
  EmoneyApprovalForm,
  EmoneyReconciliationCard,
} from "@/features/wallet/components";
import { WalletFormDialog } from "@/features/wallet/components";

function PlatformWalletBalance({
  walletId,
  currency,
}: {
  walletId: string;
  currency?: string;
}) {
  const { data } = useWalletBalance(walletId);
  return (
    <AmountMinor
      amountMinor={data?.availableBalanceMinor}
      currency={currency ?? data?.currency ?? DEFAULT_CURRENCY}
    />
  );
}

/**
 * Approvisionnement de la plateforme (guide §5.5) : wallet PLATFORM →
 * demande maker-checker → approbation par un second compte → issue/redeem
 * exécutés depuis le détail d'approbation → solde + rapprochement.
 */
export function EmoneyProvisioningPage() {
  const { t } = useTypedTranslation();
  const tw = (key: string) => t(key as TFunctionType);
  const [createOpen, setCreateOpen] = useState(false);
  const { data: platformWallets } = useWallets({
    filter: { ownerType: "PLATFORM" },
    page: 1,
    pageSize: 20,
  });
  const items = platformWallets?.items ?? [];
  const defaultWalletId = items[0]?.id;

  return (
    <section className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">
          {tw("wallet.emoney.title")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {tw("wallet.emoney.description")}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          {tw("wallet.emoney.kycHint")}
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <CardTitle className="text-base">
            {tw("wallet.emoney.platformWallets")}
          </CardTitle>
          <Button type="button" size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 size-4" />
            {tw("wallet.emoney.createPlatformWallet")}
          </Button>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {tw("wallet.emoney.noPlatformWallet")}
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {items.map((wallet) => (
                <li
                  key={wallet.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-3"
                >
                  <div className="flex flex-col">
                    <NoTranslate as="span" className="font-medium">
                      {wallet.walletNumber || wallet.id}
                    </NoTranslate>
                    <NoTranslate as="span" className="text-sm text-muted-foreground">
                      {wallet.status} · {wallet.kycLevel}
                    </NoTranslate>
                  </div>
                  {wallet.id ? (
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-muted-foreground">
                        {tw("wallet.wallets.balance.available")}
                      </span>
                      <PlatformWalletBalance
                        walletId={wallet.id}
                        currency={wallet.currency}
                      />
                      <Button asChild variant="outline" size="sm">
                        <Link to={`/app/wallet/${wallet.id}`}>
                          {tw("wallet.wallets.actions.view")}
                        </Link>
                      </Button>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <EmoneyReconciliationCard currency={DEFAULT_CURRENCY} t={tw} />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <EmoneyApprovalForm kind="issue" defaultWalletId={defaultWalletId} t={tw} />
        <EmoneyApprovalForm kind="redeem" defaultWalletId={defaultWalletId} t={tw} />
      </div>

      <p className="text-sm text-muted-foreground">
        {tw("wallet.emoney.steps.approveHint")}
      </p>

      <WalletFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        initialOwnerType="PLATFORM"
      />
    </section>
  );
}
